import { useCallback } from 'react';

import { di } from 'react-magnetic-di';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { useRovoAgentActionAnalytics } from '@atlaskit/rovo-agent-analytics/actions';
import { encodeRovoParams } from '@atlaskit/rovo-triggers/params';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { useProfileController } from '@atlassian/conversation-assistant-store/controllers/profile';

import {
	getAtlassianStudioAgentDuplicateUrl,
	getAtlassianStudioAgentEditUrl,
	getAtlassianStudioAgentEditUrlWithAri,
} from '../../common/utils/atlassian-studio-url/index.tsx';
import {
	type CrossProductBridge,
	useAgentCrossProductUrlWrapper,
} from '../../common/utils/use-agent-cross-product-url/use-agent-cross-product-url.tsx';
import { useAtlasURL } from '../atlas-url/index.ts';

import { useDuplicateAgent } from './duplicate/index.tsx';

const noopUseDuplicateAgent = { duplicateAgent: () => Promise.resolve() };

export const useAgentUrlActions = ({
	siteId,
	source,
	xpcBridge = 'rovoAgents-agentUrlActions',
}: {
	siteId: string;
	source?: string;
	/**
	 * XPC bridge = the host page/modal these agent actions are rendered in.
	 * Defaults to a generic value; hosts should pass their specific surface
	 * (e.g. 'rovoAgents-agentProfile', 'rovoAgents-browseAgentsModal').
	 */
	xpcBridge?: CrossProductBridge;
}): {
	onEditAgent: (agentId: string, agentAri?: string) => void;
	onCopyAgent: ({
		agentId,
		agentIdentityAccountId,
		shouldCopyProfilePage,
	}: {
		agentId: string;
		agentIdentityAccountId?: string | null;
		shouldCopyProfilePage?: boolean; // === Boolean(agentIdentityAccountId)
	}) => void;
	onDuplicateAgent: (agentId: string, agentAri: string) => Promise<void>;
	onViewAgentFullProfile: (agentId: string) => void;
	onStartNewChatWindowWithPrompt: (agentId: string, prompt: string) => void;
} => {
	di(
		useAnalytics,
		useProfileController,
		useDuplicateAgent,
		useAgentCrossProductUrlWrapper,
		getAtlassianStudioAgentEditUrl,
		getAtlassianStudioAgentEditUrlWithAri,
	);
	const { email } = useProfileController();
	const { atlasURL } = useAtlasURL(siteId);
	const wrapCrossProductUrl = useAgentCrossProductUrlWrapper({ xpcBridge });
	const { sendAnalyticsEvent } = useAnalytics();
	const { trackAgentEvent } = useRovoAgentActionAnalytics({
		touchPoint: source,
	});

	const { duplicateAgent } = fg('rovo_agent_versioning_enabled')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useDuplicateAgent({
				siteId,
				source,
				email,
			})
		: noopUseDuplicateAgent;

	const onEditAgent = useCallback(
		(agentId: string, agentAri?: string): void => {
			const editUrl =
				agentAri && fg('asf-1419-fix-broken-link-agent')
					? getAtlassianStudioAgentEditUrlWithAri(siteId, agentAri, source, email)
					: getAtlassianStudioAgentEditUrl(siteId, agentId, source, email);
			window.open(editUrl);
			sendAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'editAgentButton',
				// TODO: deduplicate source and interactionSource. source attribute is kept for backwards compatibility.
				attributes: { agentId, source, interactionSource: source },
			});
			fg('rovo-agents-universal-analytics') &&
				trackAgentEvent({
					action: 'edit',
					actionSubject: 'rovoAgent',
					attributes: { agentId },
				});
		},
		[email, sendAnalyticsEvent, siteId, source, trackAgentEvent],
	);

	const onCopyAgent = useCallback(
		({
			agentId,
			agentIdentityAccountId,
			shouldCopyProfilePage,
		}: {
			agentId: string;
			agentIdentityAccountId?: string | null;
			shouldCopyProfilePage?: boolean; // === Boolean(agentIdentityAccountId)
		}): void => {
			sendAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'copyAgentLinkButton',
				// TODO: deduplicate source and interactionSource. source attribute is kept for backwards compatibility.
				attributes: { agentId, source, interactionSource: source },
			});
			fg('rovo-agents-universal-analytics') &&
				trackAgentEvent({
					action: 'copyLink',
					actionSubject: 'rovoAgent',
					attributes: { agentId },
				});

			if (agentIdentityAccountId || shouldCopyProfilePage) {
				navigator.clipboard.writeText(wrapCrossProductUrl(atlasURL(`/people/agent/${agentId}`)));
				return;
			}

			const rovoParams = encodeRovoParams({
				pathway: 'chat',
				agentId: agentId,
			});
			navigator.clipboard.writeText(wrapCrossProductUrl(atlasURL(`/chat?${rovoParams}`)));
		},
		[atlasURL, sendAnalyticsEvent, source, trackAgentEvent, wrapCrossProductUrl],
	);

	const onDuplicateAgent = useCallback(
		async (agentId: string, agentAri: string): Promise<void> => {
			if (!fg('rovo_agent_versioning_enabled')) {
				window.open(getAtlassianStudioAgentDuplicateUrl(siteId, agentId, source, email));
			}
			sendAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'duplicateAgentButton',
				// TODO: deduplicate source and interactionSource. source attribute is kept for backwards compatibility.
				attributes: { agentId, source, interactionSource: source },
			});
			fg('rovo-agents-universal-analytics') &&
				trackAgentEvent({
					action: 'duplicate',
					actionSubject: 'rovoAgent',
					attributes: { agentId },
				});

			if (fg('rovo_agent_versioning_enabled')) {
				await duplicateAgent(agentId, agentAri);
			}
		},
		[duplicateAgent, sendAnalyticsEvent, source, trackAgentEvent, siteId, email],
	);

	const onViewAgentFullProfile = useCallback(
		(agentId: string): void => {
			window.open(
				wrapCrossProductUrl(atlasURL(`/people/agent/${agentId}`)),
				'_blank',
				'noopener, noreferrer',
			);

			sendAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'viewAgentFullProfileButton',
				// TODO: deduplicate source and interactionSource. source attribute is kept for backwards compatibility.
				attributes: { agentId, source, interactionSource: source },
			});
			fg('rovo-agents-universal-analytics') &&
				trackAgentEvent({
					action: 'view',
					actionSubject: 'rovoAgent',
					attributes: { agentId },
				});
		},
		[atlasURL, sendAnalyticsEvent, source, trackAgentEvent, wrapCrossProductUrl],
	);

	const onStartNewChatWindowWithPrompt = useCallback(
		(agentId: string, prompt: string): void => {
			const rovoParams = encodeRovoParams({
				pathway: 'chat',
				agentId: agentId,
				prompt,
			});
			window.open(
				wrapCrossProductUrl(atlasURL(`/chat?${rovoParams}`)),
				'_blank',
				'noopener, noreferrer',
			);
			fg('rovo-agents-universal-analytics') &&
				trackAgentEvent({
					action: 'chat',
					actionSubject: 'rovoAgent',
					attributes: { agentId },
				});
		},
		[atlasURL, trackAgentEvent, wrapCrossProductUrl],
	);

	return {
		onEditAgent,
		onCopyAgent,
		onDuplicateAgent,
		onViewAgentFullProfile,
		onStartNewChatWindowWithPrompt,
	};
};
